"use client";
import React, { useEffect, useMemo, useRef } from "react";
import styles from "../../../styles/modules/gradient.module.css";

type GradientSectionProps = {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  speed?: number;
  intensity?: number;
  bottomFade?: boolean;
  topFade?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

export function GradientSection({
  primaryColor = "#5511ef",
  secondaryColor = "#3B82F6",
  accentColor = "#A78BFA",
  speed = 1,
  intensity = 1,
  bottomFade = true,
  topFade = false,
  className,
  style,
  children,
}: GradientSectionProps) {
  const ref = useRef<HTMLElement | null>(null);
  const seed = useMemo(() => 123.456, []); // deterministic => no SSR mismatch

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    if (reduceMotion) return;

    let raf = 0;
    const start = performance.now();

    const tick = () => {
      const t = ((performance.now() - start) / 1000) * speed;

      const x1 = 28 + Math.sin(t * 0.18 + seed) * 16;
      const y1 = 22 + Math.cos(t * 0.15 + seed * 0.7) * 14;

      const x2 = 75 + Math.cos(t * 0.12 + seed * 1.1) * 14;
      const y2 = 30 + Math.sin(t * 0.16 + seed * 0.9) * 12;

      const x3 = 50 + Math.sin(t * 0.1 + seed * 1.7) * 20;
      const y3 = 78 + Math.cos(t * 0.11 + seed * 1.2) * 12;

      const rot = t * 4.5;
      const hue = Math.sin(t * 0.06 + seed * 0.2) * 8;

      el.style.setProperty("--x1", `${x1}%`);
      el.style.setProperty("--y1", `${y1}%`);
      el.style.setProperty("--x2", `${x2}%`);
      el.style.setProperty("--y2", `${y2}%`);
      el.style.setProperty("--x3", `${x3}%`);
      el.style.setProperty("--y3", `${y3}%`);
      el.style.setProperty("--rot", `${rot}deg`);
      el.style.setProperty("--hue", `${hue}deg`);

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed, seed]);

  return (
    <section
      ref={ref as any}
      className={[
        styles.wrap,
        bottomFade ? styles.bottomFade : "",
        topFade ? styles.topFade : "",
        className ?? "",
      ].join(" ")}
      style={{
        ...style,
        ["--primary" as any]: primaryColor,
        ["--secondary" as any]: secondaryColor,
        ["--accent" as any]: accentColor,
        ["--intensity" as any]: String(intensity),
      }}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.noise} aria-hidden="true" />
      <div className={styles.content}>{children}</div>
    </section>
  );
}
